import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { find } from 'ramda';
import Multilingual from '~~utils/Multilingual';
import { LeagueResource, GlossaryResource } from '~~apis/resource';
import Loading from '~~elements/Loading';
import CreateTournament from './CreateTournament';
import { CreateTournamentProvider } from './useCreateTournament';
import PageRejection from '../common/PageRejection';
import { useRootState } from '../../useRoot';

const NonOrganizerDiv = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const getPermitted = (permissionList, organizerUniqid) => {
  if (!permissionList) {
    return false;
  }

  if (permissionList.platform === 'ADMIN') {
    return true;
  }

  return !!find((l) => l.info.uniqid === organizerUniqid, permissionList.leagues);
};

const Index = () => {
  const {
    loginInfo: { isLogin },
    permissionList,
  } = useRootState();
  const { organizer_uniqid: organizerUniqid } = useParams();

  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [permitted, setPermitted] = useState(false);
  const [process, setProcess] = useState('LOADING');

  const [organizer, setOrganizer] = useState(null);
  const [locations, setLocations] = useState([]);
  const [pluginsPrice, setPluginsPrice] = useState(null);

  useEffect(() => {
    setPermitted(getPermitted(permissionList, organizerUniqid));
    setIsPlatformAdmin(permissionList?.platform === 'ADMIN');
  }, [organizerUniqid, permissionList]);

  useEffect(() => {
    if (!permitted) {
      return;
    }

    LeagueResource.getLeagueWithSeasons({ leagueUniqid: organizerUniqid })
      .then(({ data: apiOrganizer }) => {
        setOrganizer(apiOrganizer);

        let apiCountDown = 2;
        const apiDone = () => {
          apiCountDown--;
          if (apiCountDown > 0) {
            return;
          }

          setProcess('DONE');
        };

        GlossaryResource.getLocations().then(({ data: apiLocations }) => {
          setLocations(apiLocations);
          apiDone();
        });

        GlossaryResource.getTournamentPluginsSellingPrice().then(({ data: apiPluginsPrice }) => {
          setPluginsPrice(apiPluginsPrice);
          apiDone();
        });
      })
      .catch((e) => {
        setProcess('ERROR');
      });
  }, [organizerUniqid, permitted]);

  if (!isLogin) {
    return (
      <NonOrganizerDiv>
        <PageRejection text={Multilingual('COMMON.PAGE_FORBIDDEN_FOR_GUEST')} />
      </NonOrganizerDiv>
    );
  }

  if (!permissionList) {
    return (
      <NonOrganizerDiv>
        <Loading width="30vw" />
      </NonOrganizerDiv>
    );
  }

  if (!permitted) {
    return (
      <NonOrganizerDiv>
        <PageRejection text={Multilingual('COMMON.PAGE_FORBIDDEN_FOR_CURRENT_USER')} />
      </NonOrganizerDiv>
    );
  }

  switch (process) {
    case 'LOADING':
      return (
        <NonOrganizerDiv>
          <Loading width="30vw" />
        </NonOrganizerDiv>
      );
    case 'NOT_FOUND':
      return (
        <NonOrganizerDiv>
          <PageRejection text={Multilingual('COMMON.RESOURCE_NOT_FOUND')} />
        </NonOrganizerDiv>
      );
    case 'ERROR':
      return (
        <NonOrganizerDiv>
          <PageRejection text={Multilingual('COMMON.UNKNOWN_ERROR')} />
        </NonOrganizerDiv>
      );
    default:
      return (
        <CreateTournamentProvider>
          <CreateTournament
            isPlatformAdmin={isPlatformAdmin}
            organizer={organizer}
            locations={locations}
            pluginsPrice={pluginsPrice}
          />
        </CreateTournamentProvider>
      );
  }
};

export default Index;
